import { handleResponse } from './response';
import {sessionManager} from "./sessionManager";

// API timeout configuration (30 seconds)
const API_TIMEOUT_MS = 30000;

// Feature detection for fetch API
// Some older browsers (e.g., Samsung Browser before Android 5.0) don't support fetch
function checkFetchSupport() {
    if (typeof fetch === 'undefined') {
        const errorMessage = 'Den nettleseren er veldig gammel og støttes ikke! Vennligst prøv "Skann" inne i Vipps appen.';
        console.error(errorMessage);
        return {
            error: 'fetch_not_supported',
            message: errorMessage
        };
    }
    return null;
}

function errorHandler(error) {
    // Handle network errors (connection refused, no response, etc.)
    if (error.message && (error.message.includes('Failed to fetch') || 
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NetworkError'))) {
        console.error('Network error:', error.message);
        return { error: 'network_error', message: 'Kunne ikke koble til serveren. Sjekk internettforbindelsen din.' };
    }
    
    // Handle timeout errors
    if (error.message && error.message.includes('timeout')) {
        console.error('API request timeout:', error.message);
        return { error: 'timeout', message: 'Forespørselen tok for lang tid. Vennligst prøv igjen.' };
    }
    
    // Return error as-is for other cases
    return error;
}

function getAuth() {
    return 'Basic QWNjZXNzQ29udHJvbDoqXiZUR1ZCSkxPKigmXlJERkhDR1ZISkhJTyomVCpeUiUmRVlEUg==';
}

// Create a timeout promise that rejects after specified milliseconds
function createTimeoutPromise(timeoutMs) {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(`Request timeout after ${timeoutMs}ms`));
        }, timeoutMs);
    });
}

// Fetch with timeout wrapper
async function fetchWithTimeout(url, options, timeoutMs = API_TIMEOUT_MS) {
    // Check if fetch is available before attempting to use it
    const fetchError = checkFetchSupport();
    if (fetchError) {
        return Promise.reject(fetchError);
    }
    
    return Promise.race([
        fetch(url, options),
        createTimeoutPromise(timeoutMs)
    ]);
}

async function readJSON(url) {
    const requestOptions = {
        headers: getDefaultHeaders()
    };
    try {
        return await fetchWithTimeout(url, requestOptions).then(handleResponse).catch(errorHandler);
    } catch (error) {
        if (error.message && error.message.includes('timeout')) {
            console.error('API request timeout:', url);
            return { error: 'timeout', message: 'Forespørselen tok for lang tid. Vennligst prøv igjen.' };
        }
        return errorHandler(error);
    }
}

function getDefaultHeaders() {
    return {
        'Content-Type': 'application/json',
        'ServiceAuthorization': getAuth(),
        'X-Session-ID': sessionManager.getSession()
    };
}

async function getAll(url) {
    const requestOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'ServiceAuthorization': getAuth()
        }
    };

    try {
        return await fetchWithTimeout(url, requestOptions).then(handleResponse).catch(errorHandler);
    } catch (error) {
        if (error.message && error.message.includes('timeout')) {
            console.error('API request timeout:', url);
            return { error: 'timeout', message: 'Forespørselen tok for lang tid. Vennligst prøv igjen.' };
        }
        return errorHandler(error);
    }
}

async function getSingle(url, id, flagQuery) {
    const requestOptions = {
        method: 'GET',
        headers: getDefaultHeaders(),
    };

    const fullUrl = flagQuery ? `${url}${id}` : `${url}/${id}`;
    try {
        const result = await fetchWithTimeout(fullUrl, requestOptions).then(handleResponse).catch(errorHandler);
        // Check if result is an error object
        if (result && typeof result === 'object' && result.error) {
            return result;
        }
        return result;
    } catch (error) {
        return errorHandler(error);
    }
}

async function post(url, model) {
    const requestOptions = {
        method: 'POST',
        headers: getDefaultHeaders(),
        body: JSON.stringify(model)
    };
    try {
        return await fetchWithTimeout(url, requestOptions).then(handleResponse).catch(errorHandler);
    } catch (error) {
        if (error.message && error.message.includes('timeout')) {
            console.error('API request timeout:', url);
            return { error: 'timeout', message: 'Forespørselen tok for lang tid. Vennligst prøv igjen.' };
        }
        return errorHandler(error);
    }
}

async function put(url, model) {
    const requestOptions = {
        method: 'PUT',
        headers: getDefaultHeaders(),
        body: JSON.stringify(model)
    };

    try {
        return await fetchWithTimeout(url, requestOptions).then(handleResponse).catch(errorHandler);
    } catch (error) {
        if (error.message && error.message.includes('timeout')) {
            console.error('API request timeout:', url);
            return { error: 'timeout', message: 'Forespørselen tok for lang tid. Vennligst prøv igjen.' };
        }
        return errorHandler(error);
    }
}

async function remove(url, id) {
    const requestOptions = {
        method: 'DELETE',
        headers: getDefaultHeaders(),
    };

    try {
        return await fetchWithTimeout(`${url}/${id}`, requestOptions).then(handleResponse).catch(errorHandler);
    } catch (error) {
        if (error.message && error.message.includes('timeout')) {
            console.error('API request timeout:', `${url}/${id}`);
            return { error: 'timeout', message: 'Forespørselen tok for lang tid. Vennligst prøv igjen.' };
        }
        return errorHandler(error);
    }
}

export const fetchProvider = {
    getAll,
    getSingle,
    post,
    put,
    remove,
    readJSON
};